import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FMCheckboxInput from '../../../common/fm-checkbox-input/fm-checkbox-input';
import { FMButton, FMDropdown } from '@own/fds-react';
import { Option } from '@own/fds-react/dist/types/fm-dropdown.types';
import './img-spc-vehicle-service-th.scss';
import MechanicalParts from '../img-spc-mechanical-parts/img-spc-mechanical-parts';
import { ImgSpcResultsForDerivativeApiDataData } from '../../../../models/serice-price-calculator-model';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcIolmContentFragment,
    ImgSpcMechanicalPartsContentFragment,
    ImgSpcPomContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import ServiceHandler from '../../../../services/service-handler';
import { useAnalytics } from '../../../../hooks/use-analytics';
import DOMPurify from 'dompurify';

export interface ChooseYourServiceProps {
    service: any;
    intervalDropdown: any;
    serviceInterval: any;
    provinceCity: string;
    dealerCode: string;
    nameplate: string;
    engineSize: string;
    transmission: string;
    derivativeName: string;
    pomChecked: boolean;
    onPomCheckedChange: (checked: boolean) => void;
    iolmChecked: boolean;
    onIolmCheckedChange?: (checked: boolean) => void;
    freeLaborCouponChecked: boolean;
    onFreeLaborCouponCheckedChange?: (checked: boolean) => void;
    timingBeltChecked: boolean;
    onTimingBeltCheckedChange?: (checked: boolean) => void;
    mechanicalPartsChecked: boolean;
    onMechanicalPartsCheckedChange?: (isSelected: boolean) => void;
    onMechanicalPartNumbersChange?: (partNumbers: string[]) => void;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    headersLabel?: ImgSpcHeadingContentFragment;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    pomLabel?: ImgSpcPomContentFragment;
    iolmLabel?: ImgSpcIolmContentFragment;
    mechanicalpartsLabel?: ImgSpcMechanicalPartsContentFragment;
}

const ChooseYourServiceTH = ({
    service,
    intervalDropdown,
    serviceInterval,
    provinceCity,
    dealerCode,
    nameplate,
    engineSize,
    transmission,
    derivativeName,
    pomChecked,
    onPomCheckedChange,
    iolmChecked,
    onIolmCheckedChange,
    freeLaborCouponChecked,
    onFreeLaborCouponCheckedChange,
    timingBeltChecked,
    onTimingBeltCheckedChange,
    mechanicalPartsChecked,
    onMechanicalPartsCheckedChange,
    onMechanicalPartNumbersChange,
    imgSpcDerivativeData,
    headersLabel,
    fieldsLabel,
    pomLabel,
    iolmLabel,
    mechanicalpartsLabel,
}: ChooseYourServiceProps) => {
    const [hasSelected, setHasSelected] = useState(false);
    const [isTimingBeltDisabled, setIsTimingBeltDisabled] = useState(true);
    const [isLaborFree, setIsLaborFree] = useState(true);
    const [isIolmAvailable, setIsIolmAvailable] = useState(false);

    const { currentLanguageCode } = ServiceHandler.AppConfigurationService;
    const [fireEvents] = useAnalytics();

    const getYearFromInterval = useCallback(
        (intervalValue: string | Option): number => {
            const valueToParse =
                typeof intervalValue === 'object'
                    ? intervalValue.value
                    : intervalValue;

            if (valueToParse) {
                const [yearsStr] = valueToParse.split(' / ');
                return parseInt(yearsStr?.split(' ')[0] || '0');
            }
            return 0;
        },
        []
    );

    const updateTimingBeltAndLaborState = useCallback(
        (selectedValue: string | Option) => {
            const yearValue = getYearFromInterval(selectedValue);

            setIsTimingBeltDisabled(yearValue < 6);
            setIsLaborFree(yearValue <= 6);
        },
        [getYearFromInterval]
    );

    useEffect(() => {
        const allSelected = [
            provinceCity,
            dealerCode,
            nameplate,
            engineSize,
            transmission,
            derivativeName,
        ].every(value => value && value !== '');
        setHasSelected(allSelected);

        if (onMechanicalPartsCheckedChange)
            onMechanicalPartsCheckedChange(false);
        if (onMechanicalPartNumbersChange) onMechanicalPartNumbersChange([]);

        const iolmJson = imgSpcDerivativeData?.results?.[0]?.iolmJson;
        const iolmAvailable =
            iolmJson !== null &&
            typeof iolmJson === 'object' &&
            !Array.isArray(iolmJson) &&
            Object.keys(iolmJson).length > 0;
        setIsIolmAvailable(iolmAvailable);

        let currentServiceIntervalValue = '';
        if (
            serviceInterval &&
            typeof serviceInterval === 'object' &&
            serviceInterval !== null &&
            'value' in serviceInterval
        ) {
            currentServiceIntervalValue = serviceInterval.value;
        } else if (typeof serviceInterval === 'string') {
            currentServiceIntervalValue = serviceInterval;
        }

        updateTimingBeltAndLaborState(currentServiceIntervalValue);
    }, [
        provinceCity,
        dealerCode,
        nameplate,
        engineSize,
        transmission,
        derivativeName,
        imgSpcDerivativeData,
        serviceInterval,
        updateTimingBeltAndLaborState,
        onMechanicalPartsCheckedChange,
        onMechanicalPartNumbersChange,
    ]);

    useEffect(() => {
        const shouldBeDisabled =
            !iolmChecked || !isIolmAvailable || !isLaborFree;

        if (shouldBeDisabled && freeLaborCouponChecked) {
            if (onFreeLaborCouponCheckedChange) {
                onFreeLaborCouponCheckedChange(false);
            }
        }
    }, [
        iolmChecked,
        isIolmAvailable,
        isLaborFree,
        freeLaborCouponChecked,
        onFreeLaborCouponCheckedChange,
    ]);

    useEffect(() => {
        if (isTimingBeltDisabled && timingBeltChecked) {
            if (onTimingBeltCheckedChange) {
                onTimingBeltCheckedChange(false);
            }
        }
    }, [isTimingBeltDisabled, timingBeltChecked, onTimingBeltCheckedChange]);

    const handleCheckboxChange = useCallback(
        (checked: boolean) => {
            onPomCheckedChange(checked);
        },
        [onPomCheckedChange]
    );

    const handleOilChangeCheckboxChange = useCallback(
        (checked: boolean) => {
            if (onIolmCheckedChange) {
                onIolmCheckedChange(checked);
            }
            if (!checked) {
                if (onFreeLaborCouponCheckedChange)
                    onFreeLaborCouponCheckedChange(false);
            }
        },
        [onIolmCheckedChange, onFreeLaborCouponCheckedChange]
    );

    const handleFreeLaborCouponCheckboxChange = useCallback(
        (checked: boolean) => {
            if (checked && (!iolmChecked || !isIolmAvailable || !isLaborFree)) {
                return;
            }

            if (onFreeLaborCouponCheckedChange) {
                onFreeLaborCouponCheckedChange(checked);
            }
        },
        [
            onFreeLaborCouponCheckedChange,
            iolmChecked,
            isIolmAvailable,
            isLaborFree,
        ]
    );

    const handleTimingBeltCheckboxChange = useCallback(
        (checked: boolean) => {
            if (onTimingBeltCheckedChange) {
                onTimingBeltCheckedChange(checked);
            }
        },
        [onTimingBeltCheckedChange]
    );

    const handleMechanicalPartSelection = useCallback(
        (isSelected: boolean) => {
            if (onMechanicalPartsCheckedChange) {
                onMechanicalPartsCheckedChange(isSelected);
            }
        },
        [onMechanicalPartsCheckedChange]
    );

    const handleMechanicalPartNumbersReceived = useCallback(
        (partNumbers: string[]) => {
            if (onMechanicalPartNumbersChange) {
                onMechanicalPartNumbersChange(partNumbers);
            }
        },
        [onMechanicalPartNumbersChange]
    );

    const handleDropdownChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedValue = event.target.value;
            intervalDropdown(event);
            updateTimingBeltAndLaborState(selectedValue);
        },
        [intervalDropdown, updateTimingBeltAndLaborState]
    );

    const calculateButtonEnable = useMemo(() => {
        const isServiceIntervalSelected =
            serviceInterval &&
            (typeof serviceInterval === 'string'
                ? serviceInterval !== ''
                : typeof serviceInterval === 'object' &&
                  serviceInterval !== null &&
                  'value' in serviceInterval &&
                  serviceInterval.value !== '');

        return !!(
            isServiceIntervalSelected ||
            mechanicalPartsChecked ||
            (isIolmAvailable && iolmChecked)
        );
    }, [serviceInterval, mechanicalPartsChecked, isIolmAvailable, iolmChecked]);

    const renderIolmSection = useMemo(() => {
        return (
            hasSelected &&
            nameplate.startsWith('Next') &&
            !pomChecked &&
            isIolmAvailable
        );
    }, [hasSelected, nameplate, pomChecked, isIolmAvailable]);

    const isFreeLaborCouponDisabled = useMemo(() => {
        return !iolmChecked || !isIolmAvailable || !isLaborFree;
    }, [iolmChecked, isIolmAvailable, isLaborFree]);

    return (
        <>
            {hasSelected && (
                <div className="choose-service">
                    <div className="vehicle-service-pom">
                        <div className="choose-your-service">
                            {headersLabel?.serviceIntervalTitle}
                        </div>
                        {hasSelected && isIolmAvailable && (
                            <FMCheckboxInput
                                label={pomLabel?.pomService}
                                labelClassName="vehicle-inspection"
                                value="service"
                                checked={pomChecked}
                                onChange={e =>
                                    handleCheckboxChange(e.target.checked)
                                }
                                disabled={!isIolmAvailable}
                            />
                        )}
                        {pomChecked && (
                            <div
                                className="vehicle-service-description"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        pomLabel?.descriptionPOM.html ?? ''
                                    ),
                                }}
                            />
                        )}
                    </div>

                    <div className="vehicle-service-dropdown service-dropdown-margin">
                        {hasSelected && !pomChecked && (
                            <FMDropdown
                                name="serviceInterval"
                                customClass="img-th-service-dropdown"
                                header={
                                    fieldsLabel?.selectYourServiceIntervalLabel
                                }
                                value={serviceInterval}
                                options={service}
                                ariaLabel={
                                    fieldsLabel?.selectYourServiceIntervalPlaceHolderText ??
                                    'Please Select Your Service Interval'
                                }
                                onChange={handleDropdownChange}
                                disabled={service.length === 0}
                                renderOwnOption={(
                                    options: string[] | Option[]
                                ) => {
                                    const newOptions = options as Option[];
                                    return (
                                        <>
                                            <option value="">
                                                {
                                                    fieldsLabel?.selectYourServiceIntervalPlaceHolderText
                                                }
                                            </option>
                                            {newOptions.map(option => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </>
                                    );
                                }}
                            />
                        )}
                    </div>
                    <div className="vehicle-service-iolm">
                        {hasSelected &&
                        !pomChecked &&
                        imgSpcDerivativeData?.results?.[0]
                            ?.timingBeltPartDetails ? (
                            <div className="checkbox-timing-belt">
                                <FMCheckboxInput
                                    label={
                                        <>
                                            <span className="timing-belt-part-name">
                                                {currentLanguageCode.toLocaleLowerCase() ===
                                                'th'
                                                    ? imgSpcDerivativeData
                                                          .results[0]
                                                          .timingBeltPartDetails[0]
                                                          .PartNameThai
                                                    : imgSpcDerivativeData
                                                          .results[0]
                                                          .timingBeltPartDetails[0]
                                                          .PartName}{' '}
                                                -{' '}
                                            </span>
                                            {currentLanguageCode.toLocaleLowerCase() ===
                                            'th'
                                                ? imgSpcDerivativeData
                                                      .results[0]
                                                      .timingBeltPartDetails[0]
                                                      .MessageThai
                                                : imgSpcDerivativeData
                                                      .results[0]
                                                      .timingBeltPartDetails[0]
                                                      .MessageEnglish}
                                        </>
                                    }
                                    labelClassName="timing-belt"
                                    value="timingBelt"
                                    checked={timingBeltChecked}
                                    onChange={e =>
                                        handleTimingBeltCheckboxChange(
                                            e.target.checked
                                        )
                                    }
                                    disabled={isTimingBeltDisabled}
                                />
                            </div>
                        ) : null}

                        {renderIolmSection && (
                            <>
                                <div className="checkbox-oil-change">
                                    <FMCheckboxInput
                                        label={iolmLabel?.oilChangeDue}
                                        labelClassName="oil-change"
                                        value="oilChange"
                                        checked={iolmChecked}
                                        onChange={e =>
                                            handleOilChangeCheckboxChange(
                                                e.target.checked
                                            )
                                        }
                                        disabled={
                                            !isIolmAvailable || pomChecked
                                        }
                                    />
                                </div>
                                <div className="checkbox-free-labor-coupon">
                                    <FMCheckboxInput
                                        label={iolmLabel?.freeLaborCoupon}
                                        labelClassName="free-labor-coupon"
                                        value="freeLaborCoupon"
                                        checked={freeLaborCouponChecked}
                                        disabled={isFreeLaborCouponDisabled}
                                        onChange={e =>
                                            handleFreeLaborCouponCheckboxChange(
                                                e.target.checked
                                            )
                                        }
                                    />
                                </div>
                                <>
                                    <div
                                        className="vehicle-service-oil-change"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                iolmLabel
                                                    ?.descriptionToBeDisplayedForIOLM
                                                    .html ?? ''
                                            ),
                                        }}
                                    />
                                    <div
                                        className="vehicle-service-free-labour-disclosure"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                iolmLabel
                                                    ?.disclosureDataForFreeLabourCost
                                                    .html ?? ''
                                            ),
                                        }}
                                    />
                                </>
                            </>
                        )}
                        {!pomChecked && (
                            <MechanicalParts
                                onPartSelection={handleMechanicalPartSelection}
                                onMechanicalPartNumbersChange={
                                    handleMechanicalPartNumbersReceived
                                }
                                imgSpcDerivativeData={imgSpcDerivativeData}
                                mechanicalpartsLabel={mechanicalpartsLabel}
                            />
                        )}

                        {hasSelected && !pomChecked && (
                            <FMButton
                                htmlButtonType="submit"
                                label={headersLabel?.calculateButtonText}
                                type="primary"
                                className="img-spc-calculate-button"
                                disabled={!calculateButtonEnable}
                                onClick={() => {
                                    fireEvents(
                                        ['spc-calculate-cta'],
                                        undefined,
                                        {
                                            fieldName:
                                                headersLabel?.calculateButtonText,
                                        },
                                        false
                                    );
                                }}
                            />
                        )}
                    </div>
                    {hasSelected && !pomChecked && (
                        <div
                            className="other-disclaimer"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    mechanicalpartsLabel
                                        ?.disclosureDataForMechanicalParts
                                        .html ?? ''
                                ),
                            }}
                        />
                    )}
                </div>
            )}{' '}
        </>
    );
};

export default ChooseYourServiceTH;
